import {
  useGetAttachmentDocumentTypeList,
  useGetLanguageTestList,
  useGetMajorList,
  useGetSpecificSkillCertificateList,
  useGetCompanySpecificIndustryList,
} from "@/api/services/global/master";
import { IconErrorOutline } from "@/assets/icons";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { SelectColor } from "@/components/elements/SelectColor";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { TalentIndustryFields } from "@/features/talent/components/talent-form/TalentIndustryFields";
import { MAX_FILE, MAX_SIZE, TALENT_FILE_TYPES_EXTENSION, TALENT_MINE_TYPES } from "@/features/talent/constants/file";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, black, state } from "@/theme/colors";
import { FCC } from "@/types/common";
import { ATTACHED_DOCUMENT_COLOR } from "@/types/enum";
import { TExtendFile } from "@/types/file";
import { toast } from "@/utils/toast";
import { Stack, Box, Typography, Grid } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import { FileRejection } from "react-dropzone/.";
import { useFormContext, useWatch } from "react-hook-form";
import { CareIndustryValidationModal } from "@/components/commons/CareIndustryValidationModal";
import { getExtensionsOfMimeTypes, TFileRules } from "@/utils/file";
import { TalentDropZone } from "@/features/talent/components/talent-form/TalentDropZone";

type TalentFormLanguageAbilityProps = {
  onValidationCheck?: (validateFn: () => boolean) => void;
  onContinueSubmit?: () => void;
  onFileChange?: () => void;
};

export const TalentFormLanguageAbility: FCC<TalentFormLanguageAbilityProps> = ({ onValidationCheck, onContinueSubmit, onFileChange }) => {
  const methods = useFormContext<TTalentForm>();
  const { control, setValue } = methods;

  const { languageTestLevelOptions } = useGetLanguageTestList();
  const { getCertificatesByIndustryId } = useGetSpecificSkillCertificateList();
  const { majorOptions, getJobTypeListOptionByMajor } = useGetMajorList();
  const { attachmentDocumentTypeOptions } = useGetAttachmentDocumentTypeList();
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const rules: TFileRules = { acceptMimeTypes: TALENT_MINE_TYPES, acceptFileTypesExtension: TALENT_FILE_TYPES_EXTENSION };

  const companySpecificIndustryId = useWatch({ control, name: "companySpecificIndustryId" });
  const languageTestLevelIds = useWatch({ control, name: "languageTestLevelIds" });

  const [showCareIndustryValidationModal, setShowCareIndustryValidationModal] = useState(false);

  // Check if selected language test is NAT-TEST
  const isNatTestSelected = useMemo(() => {
    if (!languageTestLevelIds || !Array.isArray(languageTestLevelIds) || languageTestLevelIds.length === 0) return false;
    return languageTestLevelIds.some((id) => {
      if (id === null || id === undefined) return false;
      const selectedOption = languageTestLevelOptions.find((opt) => String(opt.value) === String(id));
      return selectedOption?.label?.includes("NAT-TEST") ?? false;
    });
  }, [languageTestLevelIds, languageTestLevelOptions]);

  const validateCareIndustryRequirement = () => {
    const currentIsCareIndustrySelected = companySpecificIndustryId
      ? (companySpecificIndustryOptions.find((opt) => Number(opt.value) === Number(companySpecificIndustryId))?.label?.includes("介護") ?? false)
      : false;

    const currentIsCareIndustryJapaneseTestSelected =
      languageTestLevelIds &&
      Array.isArray(languageTestLevelIds) &&
      languageTestLevelIds.length > 0 &&
      languageTestLevelIds.some((id) => {
        if (id === null || id === undefined) return false;
        const selectedOption = languageTestLevelOptions.find((opt) => String(opt.value) === String(id));
        return selectedOption?.label?.includes("介護日本語評価試験") ?? false;
      });

    if (currentIsCareIndustrySelected && !currentIsCareIndustryJapaneseTestSelected) {
      setShowCareIndustryValidationModal(true);
      return false;
    }
    return true;
  };

  // Function to handle modal close
  const handleModalClose = () => {
    setShowCareIndustryValidationModal(false);
  };

  // Function to handle modal continue
  const handleModalContinue = () => {
    setShowCareIndustryValidationModal(false);
    // Continue with form submission
    if (onContinueSubmit) {
      onContinueSubmit();
    }
  };

  // Expose validation function to parent
  useEffect(() => {
    if (onValidationCheck) {
      onValidationCheck(validateCareIndustryRequirement);
    }
  }, [onValidationCheck, validateCareIndustryRequirement]);

  const displayFiles = useWatch({ control, name: "displayFiles" }) || [];
  const talentUploadFiles = useWatch({ control, name: "talentUploadFiles" }) || [];
  const majorId = useWatch({ control, name: "majorId" });
  const jobTypeId = useWatch({ control, name: "jobTypeId" }) as number | null | undefined;
  const prevCompanySpecificIndustryIdRef = useRef<number | null | undefined>(undefined);
  const prevMajorIdRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    const currentPaths = methods.getValues("relatedFilePaths");
    const isInitialLoad = currentPaths === undefined || currentPaths === null;

    if (talentUploadFiles.length > 0 && displayFiles.length === 0 && isInitialLoad) {
      const displayFiles: TExtendFile[] = talentUploadFiles.map(
        (file) =>
          ({
            id: file.id,
            name: file.filePath.split("/").pop() || "",
            filePath: file.filePath,
            type: file.filePath.split(".").pop() || "",
            updatedAt: file.updatedAt,
          }) as unknown as TExtendFile,
      );

      setValue("displayFiles", displayFiles);
      setValue(
        "relatedFileTypes",
        talentUploadFiles.map((file) => String(file.attachmentDocumentTypeId)),
      );

      const existedPaths: string[] = displayFiles
        .filter((file) => "filePath" in file && file.filePath)
        .map((file) => (file as { filePath: string }).filePath as string);
      setValue("relatedFilePaths", existedPaths);
    }
  }, [talentUploadFiles, displayFiles.length, setValue, methods]);

  useEffect(() => {
    if (talentUploadFiles.length > 0 && displayFiles.length === 0) {
      return;
    }

    if (displayFiles.length > 0) {
      const existedPaths: string[] = displayFiles
        .filter((file) => "filePath" in file && file.filePath)
        .map((file) => (file as { filePath: string }).filePath as string);

      const currentPaths = methods.getValues("relatedFilePaths") || [];

      const pathsChanged =
        existedPaths.length !== currentPaths.length ||
        existedPaths.some((path, index) => path !== currentPaths[index]) ||
        currentPaths.some((path, index) => path !== existedPaths[index]);

      if (pathsChanged) {
        setValue("relatedFilePaths", existedPaths, { shouldDirty: true });
      }
    } else if (displayFiles.length === 0) {
      const currentPaths = methods.getValues("relatedFilePaths") || [];
      if (currentPaths.length > 0) {
        setValue("relatedFilePaths", [], { shouldDirty: true });
      }
    }
  }, [displayFiles, setValue, methods, talentUploadFiles.length]);

  useEffect(() => {
    if (!companySpecificIndustryId) {
      const currentValue = methods.getValues("specificSkillCertificates");
      if (currentValue !== null && currentValue !== undefined) {
        setValue("specificSkillCertificates", null, { shouldDirty: false });
      }
    }
  }, [companySpecificIndustryId, setValue, methods]);

  useEffect(() => {
    if (prevCompanySpecificIndustryIdRef.current === undefined) {
      prevCompanySpecificIndustryIdRef.current = companySpecificIndustryId;
      return;
    }

    if (prevCompanySpecificIndustryIdRef.current !== companySpecificIndustryId) {
      prevCompanySpecificIndustryIdRef.current = companySpecificIndustryId;
      setValue("specificSkillCertificates", null, { shouldDirty: false });
    }
  }, [companySpecificIndustryId, setValue]);

  useEffect(() => {
    if (prevMajorIdRef.current === undefined) {
      prevMajorIdRef.current = majorId;
      if (!majorId) {
        setValue("jobTypeId", null, { shouldDirty: false });
      }
      return;
    }

    if (prevMajorIdRef.current !== majorId) {
      prevMajorIdRef.current = majorId;

      if (!majorId) {
        setValue("jobTypeId", null, { shouldDirty: false });
        return;
      }

      const validOptions = getJobTypeListOptionByMajor(Number(majorId));
      const validOptionValues = new Set(validOptions.map((opt) => opt.value));

      if (jobTypeId !== null && jobTypeId !== undefined) {
        if (!validOptionValues.has(jobTypeId)) {
          setValue("jobTypeId", null, { shouldDirty: false });
        }
      }
    }
  }, [majorId, jobTypeId, getJobTypeListOptionByMajor, setValue]);

  const onChange = (value: TExtendFile[] | TExtendFile | null) => {
    if (value === null) {
      setValue("displayFiles", []);
      setValue("relatedFiles", []);
      setValue("relatedFilePaths", []);
      setValue("relatedFileTypes", []);
      return;
    }

    const newFiles = Array.isArray(value) ? value : [value];
    const validFiles = newFiles.filter(Boolean) as TExtendFile[];

    if (validFiles.length === 0) {
      setValue("displayFiles", []);
      setValue("relatedFiles", []);
      setValue("relatedFilePaths", []);
      setValue("relatedFileTypes", []);
      return;
    }

    const currentFileTypes = methods.getValues("relatedFileTypes") || [];
    const currentDisplayFiles = methods.getValues("displayFiles") || [];
    const currentPaths = methods.getValues("relatedFilePaths") || [];

    const idToTypeMap = new Map<string, string>();
    const pathToTypeMap = new Map<string, string>();

    currentDisplayFiles.forEach((file: TExtendFile, index: number) => {
      if (file.id && currentFileTypes[index] !== undefined && currentFileTypes[index] !== null && currentFileTypes[index] !== "") {
        idToTypeMap.set(file.id, currentFileTypes[index]);
      }

      if (file.filePath && currentFileTypes[index] !== undefined && currentFileTypes[index] !== null && currentFileTypes[index] !== "") {
        pathToTypeMap.set(file.filePath, currentFileTypes[index]);
      }
    });

    currentPaths.forEach((path: string, index: number) => {
      if (currentFileTypes[index] !== undefined && currentFileTypes[index] !== null && currentFileTypes[index] !== "") {
        pathToTypeMap.set(path, currentFileTypes[index]);
      }
    });

    // Now update displayFiles and relatedFiles
    setValue("displayFiles", validFiles);

    const uploadFiles: TExtendFile[] = validFiles.filter((file) => file instanceof File) as TExtendFile[];
    setValue("relatedFiles", uploadFiles);

    const existedPaths: string[] = validFiles
      .filter((file) => "filePath" in file && file.filePath)
      .map((file) => (file as { filePath: string }).filePath as string);

    setValue("relatedFilePaths", existedPaths);

    const newFileTypes = validFiles.map((file: TExtendFile) => {
      if (file.id && idToTypeMap.has(file.id)) {
        return idToTypeMap.get(file.id) || "";
      }
      if (file.filePath && pathToTypeMap.has(file.filePath)) {
        return pathToTypeMap.get(file.filePath) || "";
      }
      return "";
    });

    setValue("relatedFileTypes", newFileTypes);

    if (onFileChange) {
      onFileChange();
    }
  };

  const handleFileRemove = (removedIndex: number, updatedFiles: TExtendFile[]) => {
    setValue("displayFiles", updatedFiles, { shouldDirty: true });

    const uploadFiles: TExtendFile[] = updatedFiles.filter((file) => file instanceof File) as TExtendFile[];
    setValue("relatedFiles", uploadFiles, { shouldDirty: true });

    const existedPaths: string[] = updatedFiles
      .filter((file) => "filePath" in file && file.filePath)
      .map((file) => (file as { filePath: string }).filePath as string);

    setValue("relatedFilePaths", existedPaths, { shouldDirty: true });

    const currentFileTypes = methods.getValues("relatedFileTypes") || [];
    setValue("relatedFileTypes", currentFileTypes, { shouldDirty: true, shouldValidate: true });

    if (onFileChange) {
      onFileChange();
    }

    setTimeout(() => {
      currentFileTypes.forEach((value, index) => {
        setValue(`relatedFileTypes.${index}`, value, { shouldDirty: true, shouldValidate: true });
      });

      methods.trigger("relatedFileTypes").then(() => {
        currentFileTypes.forEach((_, index) => {
          methods.trigger(`relatedFileTypes.${index}`);
        });
      });
    }, 0);
  };

  const handleFileRejections = (fileRejections: FileRejection[]) => {
    const listFileErrorSize: string[] = [];
    const listFileErrorType: string[] = [];

    fileRejections.forEach((fileReject) => {
      const file = fileReject.file;
      if (file.size > MAX_SIZE) {
        listFileErrorSize.push(file.name);
      }
      const isValid = rules.acceptMimeTypes?.some((t) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        return (t === file.type || rules.acceptFileTypesExtension?.includes(ext ?? "")) ?? false;
      });
      if (!isValid) {
        listFileErrorType.push(file.name);
      }
    });

    if (listFileErrorType.length > 0) {
      const extensions = getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []);

      const messageError = `以下のファイル形式はアップロードできません。\nアップロード可能なファイル形式は${extensions.join("、")}です。`;
      const errorType = `${messageError}
      ${listFileErrorType.map((fileName) => `・${fileName}`).join("\n")}
      `;

      toast.error(errorType);
      return;
    }

    if (listFileErrorSize.length > 0) {
      const errorSize = `以下のファイルはサイズ上限（${MAX_SIZE / 1000}KB）を超えているため、アップロードできませんでした：
          ${listFileErrorSize.map((fileName) => `・${fileName}`).join("\n")}
          `;

      toast.error(errorSize);
      return;
    }
  };

  return (
    <>
      <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box flex={1}>
            <Typography variant="head20Bold" color={black[30]}>
              分野・日本語能力
            </Typography>
          </Box>
        </Stack>

        <TalentIndustryFields />

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField control={control} name="languageTestLevelIds">
                <SelectMulti placeholder="選択してください" options={languageTestLevelOptions} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="languageTestOther">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
          </Grid>
        </Stack>

        {isNatTestSelected && (
          <Stack mt={1}>
            <Typography variant="cap12" color={black[70]}>
              <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: state.warning_1 }}>
                <IconErrorOutline fontSize="20px" />
                特定技能日本語能力評価対象外（日本語能力レベル参照）
              </Box>
            </Typography>
          </Stack>
        )}

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField control={control} name="specificSkillCertificates">
                <SelectMulti
                  placeholder="選択してください"
                  options={companySpecificIndustryId ? getCertificatesByIndustryId(companySpecificIndustryId) : []}
                  separator="、"
                />
              </FormField>
            </Grid>
          </Grid>
        </Stack>
        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField control={control} name="majorId">
                <Select placeholder="選択してください" options={majorOptions} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="jobTypeId">
                <Select placeholder="選択してください" options={getJobTypeListOptionByMajor(Number(majorId))} />
              </FormField>
            </Grid>
          </Grid>
        </Stack>

        <Stack mt={3} direction="row" alignItems="center" spacing={2}>
          <Box flex={1}>
            <Typography variant="sub16Bold" color={black[30]}>
              証明書など
            </Typography>
          </Box>
        </Stack>

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TalentDropZone
                options={{ multiple: true, maxSize: MAX_SIZE }}
                bordered={true}
                rules={{
                  maxFile: MAX_FILE,
                  maxSize: MAX_SIZE,
                  acceptFileTypesExtension: rules.acceptFileTypesExtension,
                  acceptMimeTypes: rules.acceptMimeTypes,
                }}
                enableFileList={false}
                enableFileListCustom={true}
                onChange={onChange}
                onFileRemove={handleFileRemove}
                handleFileRejections={handleFileRejections}
                nonControl
                value={methods.getValues("displayFiles")}
                childrenCustom={(index) => {
                  return (
                    <FormField control={control} name={`relatedFileTypes.${index}`} sx={{ width: "260px", mr: 3, textAlign: "right" }}>
                      <SelectColor options={attachmentDocumentTypeOptions} clearable={false} optionColors={ATTACHED_DOCUMENT_COLOR} />
                    </FormField>
                  );
                }}
              ></TalentDropZone>
            </Grid>
          </Grid>
        </Stack>
      </InnerBox>

      <CareIndustryValidationModal open={showCareIndustryValidationModal} onClose={handleModalClose} onContinue={handleModalContinue} />
    </>
  );
};
